import { AsyncPipe } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { map } from "rxjs/operators";
import { AlertsService } from "src/app/core/alerts.service";
import { UserService } from "src/app/core/auth";
import { UserRegistrationService } from "src/app/core/auth/user-registration.service";
import { OrganizationTypeName } from "src/app/core/auth/user.service";
import { FormComponent } from "src/app/core/form.component";
import { FieldConfiguration } from "src/app/core/forms/field-container.component";
import {
  countriesAndStates,
  getStatesForCountry,
} from "src/app/countries-states";
import { RegistrationQueryModel } from "src/app/registration-query.model";
import { firstExistentValueFrom, phoneNumberValidator } from "src/utils";
import { ButtonComponent } from "./core/elements/button.component";
import { LoadingComponent } from "./core/elements/loading.component";
import { DropdownComponent } from "./core/forms/dropdown.component";
import { FieldsetComponent } from "./core/forms/fieldset.component";
import { GooglePlaceDirective } from "./core/forms/google-place.directive";
import { PhoneNumberInputComponent } from "./core/forms/phone-number-input.component";
import { TextareaComponent } from "./core/forms/textarea.component";
import { TextboxComponent } from "./core/forms/textbox.component";
import { PageComponent } from "./core/layout/page.component";

type RegistrationFormValue = ClassProperties<RegistrationQueryModel>;

@UntilDestroy()
@Component({
  selector: "mr-registration-form",
  templateUrl: "./registration-form.component.html",
  styleUrls: ["./registration-form.component.scss"],
  providers: [FieldConfiguration],
  imports: [
    LoadingComponent,
    PageComponent,
    FormsModule,
    ReactiveFormsModule,
    FieldsetComponent,
    TextboxComponent,
    DropdownComponent,
    PhoneNumberInputComponent,
    GooglePlaceDirective,
    TextareaComponent,
    ButtonComponent,
    AsyncPipe,
  ],
})
export class RegistrationFormComponent
  extends FormComponent<RegistrationFormValue>
  implements OnInit
{
  public constructor(
    formBuilder: UntypedFormBuilder,
    private readonly alerts: AlertsService,
    private readonly router: Router,
    private readonly user: UserService,
    private readonly userRegistration: UserRegistrationService,
  ) {
    super(formBuilder, {
      city: [null, Validators.required],
      comments: [null],
      country: ["United States", Validators.required],
      jobTitle: [null],
      mcOrDotNumber: [null],
      organizationName: [null, Validators.required],
      organizationType: [null, Validators.required],
      postalCode: [null, Validators.required],
      state: [null, Validators.required],
      streetAddress: [null, Validators.required],
      workPhoneNumber: [null, [Validators.required, phoneNumberValidator]],
    });
  }

  public readonly organizationTypesChanges =
    this.userRegistration.organizationTypesChanges;

  public readonly countryNamesChanges = countriesAndStates.map(
    (country) => country.name,
  );

  public readonly statesChanges = this.getValueChanges("country").pipe(
    map((countryName) => (countryName ? getStatesForCountry(countryName) : [])),
  );

  public readonly isNumberTypeFieldShownChanges = this.getValueChanges(
    "organizationType",
  ).pipe(
    map(
      (organizationType) =>
        !!organizationType &&
        (organizationType.name === OrganizationTypeName.Carrier ||
          organizationType.name === OrganizationTypeName.Vendor),
    ),
  );

  public readonly isJobTitleFieldShownChanges = this.getValueChanges(
    "organizationType",
  ).pipe(
    map(
      (organizationType) =>
        !!organizationType &&
        organizationType.name === OrganizationTypeName.Partner,
    ),
  );

  public ngOnInit(): void {
    this.runWithIsLoading(async () => {
      await this.user.isLoaded;

      if (!this.user.isValid) {
        if (this.user.isRegistered) {
          await this.router.navigateByUrl("/account-pending");
        }
      } else {
        await this.router.navigateByUrl("/");
      }

      if (this.user.isRegistered) {
        if (!this.user.isValid) {
          await this.router.navigateByUrl("/account-pending");
        } else {
          await this.router.navigateByUrl("/");
        }
      }

      const organizationTypes = await firstExistentValueFrom(
        this.organizationTypesChanges,
      );
      const organizationType = organizationTypes.find(
        (type) => type.name === this.user.organizationDetails.organizationType,
      );
      if (organizationType) {
        this.setValue("organizationType", organizationType);
      }

      const { organizationName, mcOrDotNumber } = this.user.organizationDetails;
      if (organizationName) {
        this.setValue("organizationName", organizationName);
      }
      if (mcOrDotNumber) {
        this.setValue("mcOrDotNumber", mcOrDotNumber);
      }
    });

    this.isJobTitleFieldShownChanges
      .pipe(untilDestroyed(this))
      .subscribe((isJobTitleFieldShown) => {
        this.reconfigure("jobTitle", {
          validators: isJobTitleFieldShown ? [Validators.required] : [],
        });
      });

    this.getValueChanges("country")
      .pipe(untilDestroyed(this))
      .subscribe((country) => {
        this.reconfigure("state", {
          value: null,
          isDisabled: !country,
        });
      });
  }

  public async submit(): Promise<void> {
    const success = await this.runSubmitWithAlerts(
      async (formValue) =>
        await firstExistentValueFrom(
          this.userRegistration.register(new RegistrationQueryModel(formValue)),
        ),
      { alerts: this.alerts },
    );

    if (success) {
      await this.router.navigateByUrl("/account-pending");
    }
  }

  public cancel(): void {
    this.user.logout();
  }
}
